import React, { useState, useEffect } from 'react';
import {
  ReceiptIndianRupee, Plus, Search, Eye, Download, Edit2, Trash2,
  ChevronLeft, ChevronRight, FileText, CheckCircle, Clock, Send,
  AlertCircle, RefreshCw, ArrowUpDown
} from 'lucide-react';
import useDebounce from '../../hooks/useDebounce';
import DateFilter from '../../components/DateFilter';
import Dropdown from '../../components/Dropdown';
import InvoiceFormModal from './InvoiceFormModal';
import InvoiceViewModal from './InvoiceViewModal';
import api, { BASE_URL } from '../../utils/api';
import toast from 'react-hot-toast';

function formatCurrency(amount) {
  const num = parseFloat(amount) || 0;
  return '₹\u00A0' + num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatDate(dateInput) {
  if (!dateInput) return '-';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

const STATUS_COLORS = {
  Draft: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  Sent: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  Approved: { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  Rejected: { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' },
  Converted: { bg: '#f3e8ff', text: '#6b21a8', border: '#d8b4fe' },
  Cancelled: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' }
};

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCount: 0,
    totalAmount: 0,
    draftCount: 0,
    sentCount: 0,
    approvedCount: 0
  });

  // Filters & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Modals
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const debouncedSearch = useDebounce(searchTerm, 400);

  // Fetch Invoices
  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });

      if (debouncedSearch && debouncedSearch.trim()) {
        params.append('search', debouncedSearch.trim());
      }
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const res = await api.get(`/proforma-invoices?${params.toString()}`);
      if (res.data) {
        setInvoices(res.data.data || []);
        if (res.data.pagination) {
          setTotalCount(res.data.pagination.total || 0);
          setTotalPages(res.data.pagination.totalPages || 1);
        }
        if (res.data.stats) {
          setStats(res.data.stats);
        }
      }
    } catch (err) {
      console.error('Failed to fetch proforma invoices', err);
      toast.error('Failed to load proforma invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, itemsPerPage, statusFilter, debouncedSearch]);

  // Handle Quick Status Change
  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await api.patch(`/proforma-invoices/${invoiceId}/status`, { status: newStatus });
      toast.success(`Invoice status updated to ${newStatus}`);
      fetchInvoices();
    } catch (err) {
      console.error('Failed to update status', err);
      toast.error('Failed to update status');
    }
  };

  // Handle Delete
  const handleDelete = async (invoiceId) => {
    try {
      await api.delete(`/proforma-invoices/${invoiceId}`);
      toast.success('Proforma invoice deleted');
      setDeleteConfirmId(null);
      fetchInvoices();
    } catch (err) {
      console.error('Failed to delete invoice', err);
      toast.error('Failed to delete invoice');
    }
  };

  // Open View Modal
  const handleOpenView = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (invoice) => {
    setInvoiceToEdit(invoice);
    setShowFormModal(true);
  };

  // Open Create Modal
  const handleOpenCreate = () => {
    setInvoiceToEdit(null);
    setShowFormModal(true);
  };

  // Download PDF
  const handleDownloadPdf = (invoice) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const url = `${BASE_URL}/v1/proforma-invoices/${invoice.id}/pdf?download=true&token=${token}`;
    window.open(url, '_blank');
  };

  return (
    <div className="page-container">
      
      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--color-text-main)', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <ReceiptIndianRupee size={28} color="var(--color-primary)" />
            Proforma Invoices
          </h1>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
            Create and manage customer proforma invoices with GST breakdown, bank details, and instant print/PDF.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="btn btn-primary"
          style={{ gap: '6px', padding: '0.55rem 1.1rem', fontSize: '0.9rem' }}
        >
          <Plus size={18} /> Create Proforma Invoice
        </button>
      </div>

      {/* Stats Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div className="card" style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'var(--color-primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ReceiptIndianRupee size={22} color="var(--color-primary)" />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Total Invoices</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--color-text-main)' }}>{stats.totalCount || 0}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 600 }}>{formatCurrency(stats.totalAmount || 0)}</div>
          </div>
        </div>

        <div className="card" style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={22} color="#b45309" />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Draft Invoices</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#b45309' }}>{stats.draftCount || 0}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Pending dispatch</div>
          </div>
        </div>

        <div className="card" style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Send size={22} color="#1d4ed8" />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Sent Invoices</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#1d4ed8' }}>{stats.sentCount || 0}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Shared with customer</div>
          </div>
        </div>

        <div className="card" style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle size={22} color="#15803d" />
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Approved Invoices</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#15803d' }}>{stats.approvedCount || 0}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Confirmed orders</div>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="card" style={{ padding: '1rem', marginBottom: '1.25rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          {/* Search Box */}
          <div style={{ position: 'relative', minWidth: '260px', flex: '1 1 300px' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-muted)'
              }}
            />
            <input
              type="text"
              className="input-field"
              placeholder="Search by Invoice No, Customer, Phone, GSTIN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '38px', height: '38px' }}
            />
          </div>

          {/* Status Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', fontWeight: 500 }}>Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="input-field"
              style={{ height: '38px', width: 'auto', paddingRight: '2rem' }}
            >
              <option value="all">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Sent">Sent</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Converted">Converted</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <button
            onClick={fetchInvoices}
            className="btn"
            style={{ height: '38px', padding: '0 12px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
            title="Refresh list"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid var(--color-border)' }}>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Invoice #</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Date</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Customer Details</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Items</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Taxable</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569', textAlign: 'right' }}>Grand Total</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Status</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <RefreshCw size={18} className="animate-spin" /> Loading proforma invoices...
                    </div>
                  </td>
                </tr>
              ) : invoices.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: '3.5rem 1rem', textAlign: 'center' }}>
                    <ReceiptIndianRupee size={40} style={{ color: '#cbd5e1', marginBottom: '8px' }} />
                    <div style={{ fontWeight: 600, color: '#475569', fontSize: '1rem' }}>No Proforma Invoices Found</div>
                    <div style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '4px 0 16px 0' }}>
                      {searchTerm || statusFilter !== 'all'
                        ? 'Try adjusting your search or filters'
                        : 'Create your first professional proforma invoice for Abhay Agency'}
                    </div>
                    <button onClick={handleOpenCreate} className="btn btn-primary" style={{ gap: '6px' }}>
                      <Plus size={16} /> Create Proforma Invoice
                    </button>
                  </td>
                </tr>
              ) : (
                invoices.map((inv) => {
                  const statusStyle = STATUS_COLORS[inv.status] || STATUS_COLORS.Draft;
                  const itemsCount = inv.items ? inv.items.length : 0;
                  const firstItemName = inv.items && inv.items[0] ? inv.items[0].name : '';

                  return (
                    <tr
                      key={inv.id}
                      style={{ borderBottom: '1px solid var(--color-border)', transition: 'background 0.15s ease' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {/* Invoice Number */}
                      <td style={{ padding: '12px 16px' }}>
                        <button
                          onClick={() => handleOpenView(inv)}
                          style={{
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            color: 'var(--color-primary)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            padding: 0
                          }}
                        >
                          {inv.invoiceNumber}
                        </button>
                        {inv.lead && (
                          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>CRM Lead #{inv.lead.id}</div>
                        )}
                      </td>

                      {/* Date */}
                      <td style={{ padding: '12px 16px', color: '#334155' }}>
                        <div>{formatDate(inv.invoiceDate)}</div>
                      </td>

                      {/* Customer */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{inv.customerName}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>
                          {inv.customerPhone && <span>{inv.customerPhone}</span>}
                          {inv.customerPhone && inv.billingCity && <span> • </span>}
                          {inv.billingCity && <span>{inv.billingCity}</span>}
                        </div>
                        {inv.customerGst && (
                          <span style={{ fontSize: '0.65rem', background: '#f1f5f9', padding: '1px 5px', borderRadius: '4px', color: '#475569', marginTop: '2px', display: 'inline-block' }}>
                            GST: {inv.customerGst}
                          </span>
                        )}
                      </td>

                      {/* Items */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 500, color: '#334155' }}>
                          {itemsCount} {itemsCount === 1 ? 'item' : 'items'}
                        </div>
                        {firstItemName && (
                          <div style={{ fontSize: '0.72rem', color: '#64748b', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>
                            {firstItemName}
                          </div>
                        )}
                      </td>

                      {/* Taxable */}
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: '#64748b', whiteSpace: 'nowrap' }}>
                        {formatCurrency(inv.subtotal)}
                      </td>

                      {/* Grand Total */}
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>
                        {formatCurrency(inv.grandTotal)}
                      </td>

                      {/* Status */}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <select
                          value={inv.status || 'Draft'}
                          onChange={(e) => handleStatusChange(inv.id, e.target.value)}
                          style={{
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.text,
                            border: `1px solid ${statusStyle.border}`,
                            borderRadius: '9999px',
                            padding: '3px 10px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            outline: 'none',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          <option value="Draft">Draft</option>
                          <option value="Sent">Sent</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Converted">Converted</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </td>

                      {/* Action Buttons */}
                      <td style={{ padding: '0.65rem 0.75rem', textAlign: 'center' }}>
                        <div className="flex gap-2 justify-center">
                          <button
                            className="btn text-primary"
                            style={{ padding: '0.3rem' }}
                            onClick={() => handleOpenView(inv)}
                            title="View Invoice"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="btn text-primary"
                            style={{ padding: '0.3rem' }}
                            onClick={() => handleDownloadPdf(inv)}
                            title="Download PDF"
                          >
                            <Download size={18} />
                          </button>
                          <button
                            className="btn text-muted"
                            style={{ padding: '0.3rem' }}
                            onClick={() => handleOpenEdit(inv)}
                            title="Edit Invoice"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            className="btn text-danger"
                            style={{ padding: '0.3rem' }}
                            onClick={() => setDeleteConfirmId(inv.id)}
                            title="Delete Invoice"
                          >
                            <Trash2 size={18} color="var(--color-danger)" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalCount > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--color-border)',
            flexWrap: 'wrap',
            gap: '10px',
            fontSize: '0.85rem',
            color: 'var(--color-text-muted)'
          }}>
            <div>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} invoices
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="btn"
                style={{ padding: '0.35rem 0.6rem', background: '#f1f5f9', border: '1px solid #cbd5e1' }}
              >
                <ChevronLeft size={16} />
              </button>
              <span style={{ padding: '0 8px', fontWeight: 600, color: 'var(--color-text-main)' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage >= totalPages}
                className="btn"
                style={{ padding: '0.35rem 0.6rem', background: '#f1f5f9', border: '1px solid #cbd5e1' }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="modal-backdrop" onClick={() => setDeleteConfirmId(null)} style={{ zIndex: 11000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ padding: '1.5rem', maxWidth: '420px', textAlign: 'center' }}>
            <AlertCircle size={44} color="#ef4444" style={{ marginBottom: '10px' }} />
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.15rem', color: '#1e293b' }}>Delete Proforma Invoice?</h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.875rem', color: '#64748b' }}>
              Are you sure you want to delete this proforma invoice? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button
                className="btn"
                onClick={() => setDeleteConfirmId(null)}
                style={{ background: '#f1f5f9', color: '#475569' }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => handleDelete(deleteConfirmId)}
                style={{ background: '#ef4444', color: '#ffffff' }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal (Create / Edit) */}
      <InvoiceFormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        invoiceToEdit={invoiceToEdit}
        onSaved={fetchInvoices}
      />

      {/* View & Print Modal */}
      <InvoiceViewModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        invoice={selectedInvoice}
        onEdit={(inv) => handleOpenEdit(inv)}
        onStatusChanged={fetchInvoices}
      />

    </div>
  );
};

export default InvoiceList;
